import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import type { MenuItem } from '../Menu';
import { SkeletonRows } from '../Skeleton';
import { AnchorCellContext } from '../table/anchorCell';
import { HeaderTooltip } from '../table/HeaderTooltip';
import { IndexTableSurfaceContext } from '../table/indexTableSurface';
import { RowActionMenu } from '../table/RowActionMenu';
import { RowQuickActions } from '../table/RowQuickActions';
import type { QuickActionStyle } from '../table/rowQuickActionWidth';
import { quickActionsWidth } from '../table/rowQuickActionWidth';
import type { OtherInfoData } from '../table/OtherInfoModal';
import { useColumnWidths } from '../table/useColumnWidths';
import { useRowHeight } from '../table/useRowHeight';
import type { ColumnDef } from './types';
import { Checkbox } from '../inputs/Checkbox';

const SELECT_WIDTH = 44;
const ROW_NUMBER_WIDTH = 52;

/** The gutter with a kebab in it and nothing else. */
const ACTIONS_WIDTH = 56;

/*
 * What each quick action adds to that gutter is no longer a constant — a quick action is a
 * labelled `<Button>`, so two of them on one row are not the same width. `quickActionsWidth`
 * measures them.
 *
 * **The gutter has to GROW for them, and this is not cosmetic.** The table is
 * `table-layout: fixed` with an explicit `<colgroup>`, so the column is exactly as wide as it
 * is told: quick actions rendered into a 56px cell sized for one kebab overflow it and are
 * clipped, which reads as buttons that are drawn and do not work.
 */

/**
 * The medicine-table format — reference §4. One tabular look everywhere.
 *
 * Fixed layout with a colgroup, a page-aware `#` gutter, resizable columns, a sticky
 * header, a sticky totals `<tfoot>`, opt-in multi-select, and the Actions column always
 * last and pinned right.
 *
 * The anchor column — the first after the `#` gutter — is the row's identity and its click
 * target. Whether it is ALSO pinned to the left edge is a separate question with a separate
 * prop, `freezeAnchor`, off by default.
 */
export function IndexTable<T>({
  module,
  columns,
  rows,
  rowKey,
  firstRow,
  loading,
  sort,
  onToggleSort,
  otherInfo,
  rowActions,
  rowQuickActions,
  rowQuickActionStyle = 'label',
  onEdit,
  editPermission,
  onDelete,
  deletePermission,
  deleteLabel,
  deleteDisabledReason,
  selectable = false,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  onRowOpen,
  freezeAnchor = false,
  groupRow,
}: {
  module: string;
  columns: ColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  firstRow: number;
  loading: boolean;
  sort: string | null;
  onToggleSort: (field: string) => void;
  otherInfo: (row: T) => OtherInfoData | null;
  rowActions?: (row: T) => MenuItem[];
  /**
   * A row's most-used actions, as small labelled buttons before the kebab — see `<RowQuickActions>`.
   *
   * Entries here are PROMOTED OUT of `rowActions`, never duplicated into it: one action in two
   * places on one row is what this product refuses everywhere else.
   */
  rowQuickActions?: (row: T) => MenuItem[];
  /** `icon` is the Repository exception — see `IndexPageProps.rowQuickActionStyle`. */
  rowQuickActionStyle?: QuickActionStyle;
  onEdit?: (row: T) => void;
  editPermission?: string;
  onDelete?: (row: T) => void;
  deletePermission?: string;
  deleteLabel?: (row: T) => string;
  deleteDisabledReason?: (row: T) => string | null;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onToggleRow?: (key: string) => void;
  onToggleAll?: () => void;
  /**
   * Opens the record from the anchor column. Supplying it is what makes that column
   * clickable — without it the cell stays darker but inert, never a false affordance.
   */
  onRowOpen?: (row: T) => void;
  /**
   * Pins the anchor column to the left edge while the table scrolls sideways. Off by
   * default: it earns its keep only on a table wide enough to lose sight of its subject,
   * and costs a visible seam plus a stacking context on every other one.
   */
  freezeAnchor?: boolean;
  /**
   * A full-width GROUP HEADER ROW wherever `key` changes — `<DataTable>`'s `groupRow`, the same
   * shape. The SERVER must order by the key, and the page's columns must not sort, or a group
   * splits into several. Table view only.
   */
  groupRow?: { key: (row: T) => string; render: (row: T) => ReactNode };
}) {
  const defaults = columns.map((c) => c.width);
  const { widths, startResize } = useColumnWidths(module, columns);

  /*
    Row height is the other half of the same control: both axes are dragged, both persist
    per table, and both are the reader's call rather than the page's.
  */
  const { height: rowHeight, startResize: startRowResize } = useRowHeight(module);

  const [sortField, sortDirection] = sort ? sort.split(':') : [null, null];

  // Any column declaring a footer turns the totals row on for the whole table.
  const hasFooter = columns.some((c) => c.footer);

  /*
    The table is exactly as wide as its columns add up to.

    It used to be `width: max-content`, which sounds equivalent and is not: max-content sizes
    the table from its CONTENT, so a value wider than its column simply widened the column —
    the colgroup asked for 80px and got 331px. Column widths were advisory, a drag could be
    silently undone by one long value, and no cell ever overflowed, so neither the ellipsis
    nor the wrap rule could ever fire.

    With an explicit total, `minWidth: 100%` still stretches a narrow table to fill the panel,
    and the browser hands that slack to the width-less soft-flex column — which is the
    behaviour the flex column was always meant to have.
  */
  /*
    The gutter is sized for what is actually IN it — the kebab, plus a slot per quick action.

    Counted across the rendered rows rather than assumed, because a page may offer an action on
    some rows and not others. It counts entries BEFORE `<RowQuickActions>` filters them by
    permission, so a reader lacking a key gets a slightly wide gutter rather than a clipped one:
    over-wide is invisible, under-wide is a button that cannot be pressed.
  */
  const quickActionsGutter =
    rowQuickActions && rows.length > 0
      ? Math.max(...rows.map((row) => quickActionsWidth(rowQuickActions(row), rowQuickActionStyle)))
      : 0;

  const actionsWidth = ACTIONS_WIDTH + quickActionsGutter;

  const totalWidth =
    (selectable ? SELECT_WIDTH : 0) +
    ROW_NUMBER_WIDTH +
    columns.reduce((sum, column, index) => sum + (widths[index] ?? column.width), 0) +
    actionsWidth;

  const selected = selectedKeys ?? new Set<string>();
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const someSelected = rows.some((r) => selected.has(rowKey(r))) && !allSelected;

  // The leading gutters are always pinned; the anchor joins them only if asked.
  const frozenOffset = selectable ? SELECT_WIDTH : 0;


  return (
    // Declares the surface so cells can refuse shapes that belong to other table contexts.
    <IndexTableSurfaceContext.Provider value={true}>
      {/*
        Stretched to the panel ONLY when there is a sticky tfoot to hold on the bottom edge.

        `height: 100%` on a table hands its surplus to the ROW boxes, distributed across
        them: a two-row list in a 700px panel rendered two 320px rows. So the stretch is now
        conditional, and the spacer row at the end of the tbody absorbs the slack — the tfoot
        still sits on the bottom edge of a short table, and the rows above it keep the height
        they asked for.
      */}
      <table
        className="border-collapse"
        style={{
          tableLayout: 'fixed',
          width: totalWidth,
          minWidth: '100%',
          height: hasFooter ? '100%' : undefined,
        }}
      >
        <colgroup>
          {selectable ? <col style={{ width: SELECT_WIDTH }} /> : null}
          <col style={{ width: ROW_NUMBER_WIDTH }} />
          {columns.map((column, index) => (
            <col
              key={column.key}
              // The soft-flex anchor stays width-less until dragged, so it absorbs slack.
              style={
                column.flex && widths[index] === defaults[index]
                  ? undefined
                  : { width: widths[index] ?? column.width }
              }
            />
          ))}
          <col style={{ width: actionsWidth }} />
        </colgroup>

        {/*
          The weight is `[&>th]:font-normal`, on the CELLS, not a `font-normal` on this row.
          A `<th>` carries the browser's own `font-weight: bold`, and Tailwind's preflight
          resets that for `h1`–`h6` but never for `th` — a rule applied directly to an element
          beats a value inherited from its parent, whatever the origin. So a font weight set
          here never reaches the header cells at all: the `font-semibold` that used to sit on
          this row was inert, and the headers were rendering at the browser's 700 rather than
          the 600 the class claimed. Swapping that class to `font-normal` would have been
          equally inert, which is the trap this comment exists to stop.

          Every other class here does inherit — `th` has no user-agent rule for size, case,
          tracking or colour — which is why only the weight moves onto the cells.
        */}
        <thead className="sticky top-0 z-20">

          <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
            {selectable ? (
              <th
                scope="col"
                className="sticky left-0 z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0"
                style={{ width: SELECT_WIDTH }}
              >
                <Checkbox
                  id={`${module}-select-all`}
                  name={`${module}-select-all`}
                  checked={allSelected}
                    indeterminate={someSelected}
                  onChange={onToggleAll}
                  aria-label="Select all rows on this page"
                />
              </th>
            ) : null}

            {/* The # gutter stays put so a row keeps its number while scrolling sideways. */}
            <th
              scope="col"
              className="sticky z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 text-left first:border-l-0"
              style={{ left: frozenOffset, width: ROW_NUMBER_WIDTH }}
            >
              #

              {/*
                Row height hangs off the # gutter's BOTTOM edge. It is the one column that is
                about rows rather than about a value, so a horizontal grip here cannot be
                mistaken for a column resizer — and `sticky` already positions this cell, so
                the handle needs no `relative` of its own.
              */}
              <span
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize row height"
                onPointerDown={startRowResize}
                className="absolute -bottom-0.5 left-0 z-10 h-1.5 w-full cursor-row-resize hover:bg-blue-300"
              />
            </th>

            {columns.map((column, index) => {
              const isSorted = sortField === column.key;
              const isFrozen = freezeAnchor && index === 0;

              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={
                    isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  className={[
                    'relative border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0',
                    column.align === 'right' ? 'text-right' : 'text-left',
                    isFrozen ? 'sticky z-30' : '',
                  ].join(' ')}
                  style={isFrozen ? { left: frozenOffset + ROW_NUMBER_WIDTH } : undefined}
                >
                  <HeaderTooltip tooltip={column.headerTooltip}>
                    {column.sortable ? (
                      <button
                        type="button"
                        id={`${module}-sort-${column.key}`}
                        onClick={() => onToggleSort(column.key)}
                        /*
                          The glyph sits on the column's OUTER RIGHT, not against the label.
                          Beside the text it moved with every header's length, so a row of
                          headers had its arrows scattered at six different x positions and the
                          reader had to find each one; pinned to the edge they line up down the
                          right of every sortable column and read as one control per column.

                          A right-aligned column keeps `justify-end`, so its label stays right
                          and the glyph is simply outermost — `justify-between` there would drag
                          the label back to the left and break the alignment the column chose.
                        */
                        className={[
                          'flex w-full items-center gap-1 uppercase tracking-wider transition hover:text-slate-700',
                          column.align === 'right' ? 'justify-end' : 'justify-between',
                        ].join(' ')}
                      >
                        {/* `min-w-0` so a long header wraps as before instead of pushing the
                            glyph out of the cell. */}
                        <span className="min-w-0">{column.header}</span>
                        {isSorted ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="h-3 w-3 shrink-0" aria-hidden="true" />
                          ) : (
                            <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
                          )
                        ) : (
                          <ChevronsUpDown
                            className="h-3 w-3 shrink-0 text-slate-400"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    ) : (
                      <span>{column.header}</span>
                    )}
                  </HeaderTooltip>

                  {/*
                    Drag handle on the right edge. No adjacent-column compensation — the
                    table widens instead, which is what makes a drag feel predictable.
                  */}
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${column.header}`}
                    onPointerDown={(e) => startResize(index, e)}
                    className="absolute -right-0.5 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-blue-300"
                  />
                </th>
              );
            })}

            {/*
              Actions is always last, fixed width, and pinned to the right edge. Pinning is
              not decoration: once a column is widened the table scrolls horizontally, and an
              unpinned kebab ends up off-screen — unreachable without scrolling sideways to
              reach the primary way to act on a row.
            */}
            <th
              scope="col"
              className="sticky right-0 z-30 border-l border-slate-200 bg-slate-100 px-2 py-2 text-right"
              style={{ width: actionsWidth }}
            >
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <SkeletonRows
              rows={8}
              columns={columns.length + (selectable ? 3 : 2)}
              height={rowHeight}
            />
          ) : (
            rows.map((row, index) => {
              const key = rowKey(row);
              const isSelected = selected.has(key);

              return (
                <Fragment key={key}>
                {groupRow && (index === 0 || groupRow.key(rows[index - 1]) !== groupRow.key(row)) ? (
                  <tr className="border-t border-slate-200 bg-slate-50">
                    {/* Every gutter and column: select, the `#` gutter, the data, the Actions gutter. */}
                    <td colSpan={(selectable ? 1 : 0) + 1 + columns.length + 1} className="px-3 py-2 align-middle">
                      {groupRow.render(row)}
                    </td>
                  </tr>
                ) : null}
                <tr
                  // A floor, not a clamp: a wrapping cell still grows the row past it.
                  style={{ height: rowHeight }}
                  className={`group border-t border-slate-200 hover:bg-slate-50 ${
                    isSelected ? 'bg-blue-50/40' : ''
                  }`}
                >
                  {selectable ? (
                    <td
                      className="sticky left-0 z-10 border-l border-slate-200 bg-white px-3 py-2 first:border-l-0 group-hover:bg-slate-50"
                      style={{ width: SELECT_WIDTH }}
                    >
                      <Checkbox
                        id={`${module}-select-${key}`}
                        name={`${module}-select-${key}`}
                        checked={isSelected}
                        onChange={() => onToggleRow?.(key)}
                        aria-label="Select row"
                      />
                    </td>
                  ) : null}

                  <td
                    className="sticky z-10 border-l border-slate-200 bg-white px-3 py-2 font-mono tabular-nums text-xs text-slate-400 first:border-l-0 group-hover:bg-slate-50"
                    style={{ left: frozenOffset, width: ROW_NUMBER_WIDTH }}
                  >
                    {firstRow + index}
                  </td>

                  {columns.map((column, columnIndex) => {
                    // Identity and position are separate questions about the same column: the anchor is
                    // always the row's click target, whether or not it is pinned. It is the column that
                    // asks to be (`anchor`), else the first; the pin's offset is the first column's, so
                    // only a FIRST-column anchor is pinned.
                    const isAnchor = columnIndex === Math.max(0, columns.findIndex((c) => c.anchor));
                    const isFrozen = freezeAnchor && isAnchor && columnIndex === 0;

                    return (
                      <td
                        key={column.key}
                        className={[
                          /*
                            THE TONE IS THE SHELL'S. With none set the cell INHERITS `body`'s `#0f172a` —
                            the value this product reserves for whatever is SELECTED — so any column whose
                            `render` returns a bare string painted darker than the heading above it, and
                            read as deliberate because every such row agreed. `<TextCell>` sets its own tone
                            on the span inside and still wins; this is the floor for the renderers that set
                            none. `<PreviewGrid>` already took this fix; the two table shells had not.
                          */
                          'border-l border-slate-200 px-3 py-2 align-middle text-slate-500 first:border-l-0',
                          column.align === 'right' ? 'text-right' : '',
                          isFrozen ? 'sticky z-10 bg-white group-hover:bg-slate-50' : '',
                        ].join(' ')}
                        style={isFrozen ? { left: frozenOffset + ROW_NUMBER_WIDTH } : undefined}
                      >
                        {isAnchor ? (
                          <AnchorCellContext.Provider value={true}>
                            {onRowOpen ? (
                              <button
                                type="button"
                                id={`${module}-open-${key}`}
                                onClick={() => onRowOpen(row)}
                                className="block w-full min-w-0 cursor-pointer rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                              >
                                {column.render(row, firstRow + index)}
                              </button>
                            ) : (
                              column.render(row, firstRow + index)
                            )}
                          </AnchorCellContext.Provider>
                        ) : (
                          column.render(row, firstRow + index)
                        )}
                      </td>
                    );
                  })}

                  {/*
                    The quick actions share the Actions gutter with the kebab rather than taking
                    a column of their own — they ARE row actions, promoted out of the menu, so a
                    column would put one kind of thing in two places and cost the data its width.
                  */}
                  <td className="sticky right-0 z-10 border-l border-slate-200 bg-white px-2 py-2 group-hover:bg-slate-50">
                    <div className="flex items-center justify-end gap-1">
                      {rowQuickActions ? (
                        <RowQuickActions
                          id={`${module}-quick-${key}`}
                          items={rowQuickActions(row)}
                          style={rowQuickActionStyle}
                        />
                      ) : null}

                      <RowActionMenu
                        id={`${module}-actions-${key}`}
                        otherInfo={otherInfo(row)}
                        extraItems={rowActions?.(row)}
                        onEdit={onEdit ? () => onEdit(row) : undefined}
                        editPermission={editPermission}
                        onDelete={onDelete ? () => onDelete(row) : undefined}
                        deletePermission={deletePermission}
                        deleteLabel={deleteLabel?.(row)}
                        deleteDisabledReason={deleteDisabledReason?.(row) ?? null}
                      />
                    </div>
                  </td>
                </tr>
                </Fragment>
              );
            })
          )}

          {/*
            Takes the stretch that would otherwise inflate every data row. Only present when
            the table is stretched at all, so an ordinary list carries no phantom row.

            ONE CELL PER COLUMN, never a `colSpan` — the grid has to run to the footer. A single
            spanning cell draws no vertical rules, so the column lines stopped under the last row
            and the band above the totals was blank. The sticky gutters keep their offsets and
            their ground: an empty cell still paints over horizontally scrolled content, and one
            placed at the wrong `left` puts its rule in the wrong column while the reader scrolls.
          */}
          {hasFooter && !loading ? (
            <tr aria-hidden="true" className="h-full">
              {selectable ? (
                <td
                  className="sticky left-0 z-10 border-l border-slate-200 bg-white p-0 first:border-l-0"
                  style={{ width: SELECT_WIDTH }}
                />
              ) : null}

              <td
                className="sticky z-10 border-l border-slate-200 bg-white p-0 first:border-l-0"
                style={{ left: frozenOffset, width: ROW_NUMBER_WIDTH }}
              />

              {columns.map((column, index) => {
                const isFrozen = freezeAnchor && index === 0;

                return (
                  <td
                    key={column.key}
                    className={[
                      'border-l border-slate-200 p-0 first:border-l-0',
                      isFrozen ? 'sticky z-10 bg-white' : '',
                    ].join(' ')}
                    style={isFrozen ? { left: frozenOffset + ROW_NUMBER_WIDTH } : undefined}
                  />
                );
              })}

              <td className="sticky right-0 z-10 border-l border-slate-200 bg-white p-0" />
            </tr>
          ) : null}
        </tbody>

        {/* Totals pinned to the bottom edge, so a scrolled list keeps its summary in view. */}
        {hasFooter && !loading ? (
          <tfoot className="sticky bottom-0 z-20">
            <tr className="bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600">
              {selectable ? (
                <td
                  className="sticky left-0 z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0"
                  style={{ width: SELECT_WIDTH }}
                />
              ) : null}

              <td
                className="sticky z-30 border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0"
                style={{ left: frozenOffset, width: ROW_NUMBER_WIDTH }}
              />

              {columns.map((column, index) => {
                const isFrozen = freezeAnchor && index === 0;

                return (
                  <td
                    key={column.key}
                    className={[
                      'border-l border-slate-200 bg-slate-100 px-3 py-2 first:border-l-0',
                      column.align === 'right' ? 'text-right' : 'text-left',
                      isFrozen ? 'sticky z-30' : '',
                    ].join(' ')}
                    style={isFrozen ? { left: frozenOffset + ROW_NUMBER_WIDTH } : undefined}
                  >
                    {column.footer?.(rows)}
                  </td>
                );
              })}

              <td className="sticky right-0 z-30 border-l border-slate-200 bg-slate-100 px-2 py-2" />
            </tr>
          </tfoot>
        ) : null}
      </table>
    </IndexTableSurfaceContext.Provider>
  );
}

